import { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Mono, Modal } from '@/design/primitives';
import { color, fontSize, fontWeight, space } from '@/design/tokens';
import { useI18n } from '@/i18n';
import { useSession } from '@/lib/session';
import { authApi } from './api';

/** How often the countdown is re-checked against the server, in ms. */
const POLL_INTERVAL_MS = 30_000;
/** How often the local countdown ticks between polls. */
const TICK_MS = 1_000;

/**
 * The idle-timeout warning (AUTH-010).
 *
 * The timer that matters is the server's - `GET /auth/session` reports the
 * remaining seconds and, deliberately, does not extend the window. This
 * component therefore polls rather than running its own clock as the source of
 * truth; a purely client-side timer disagrees with the server the moment a
 * laptop sleeps, and the user is signed out with no warning at all.
 *
 * Across tabs comes for free: the window lives in Redis keyed by user, so every
 * tab reads and extends the same one. Activity in any tab keeps all of them
 * alive, which is the behaviour a clinician with the schedule open on one screen
 * and a visit on another actually needs.
 */
export function IdleTimeoutModal() {
  const { t } = useI18n();
  const { user, signOut } = useSession();

  const [secondsRemaining, setSecondsRemaining] = useState<number | null>(null);
  const [warnAt, setWarnAt] = useState(120);
  const [extending, setExtending] = useState(false);
  const signedOut = useRef(false);

  const poll = useCallback(async () => {
    if (!user) return;
    try {
      const session = await authApi.session();
      setWarnAt(session.warnAtSeconds);
      setSecondsRemaining(session.secondsRemaining);
    } catch {
      // A failed poll is not a reason to sign anyone out. The request the user
      // makes next will get the real answer from the server.
    }
  }, [user]);

  // Poll while signed in.
  useEffect(() => {
    if (!user) {
      setSecondsRemaining(null);
      return;
    }
    void poll();
    const id = window.setInterval(() => void poll(), POLL_INTERVAL_MS);
    return () => window.clearInterval(id);
  }, [user, poll]);

  // Tick locally between polls so the countdown does not jump in 30s steps.
  useEffect(() => {
    if (secondsRemaining === null) return;
    const id = window.setInterval(() => {
      setSecondsRemaining((current) => (current === null ? null : Math.max(0, current - 1)));
    }, TICK_MS);
    return () => window.clearInterval(id);
  }, [secondsRemaining === null]);

  // Any real interaction is already extending the window server-side, because
  // every authenticated request does. This only re-syncs the display.
  useEffect(() => {
    if (!user) return;
    const onActivity = () => {
      if (secondsRemaining !== null && secondsRemaining > warnAt) void poll();
    };
    window.addEventListener('focus', onActivity);
    return () => window.removeEventListener('focus', onActivity);
  }, [user, poll, secondsRemaining, warnAt]);

  const expired = user !== null && secondsRemaining !== null && secondsRemaining <= 0;

  useEffect(() => {
    if (expired && !signedOut.current) {
      signedOut.current = true;
      void signOut();
    }
  }, [expired, signOut]);

  if (!user || secondsRemaining === null) return null;
  const shouldWarn = secondsRemaining > 0 && secondsRemaining <= warnAt;
  if (!shouldWarn) return null;

  const minutes = Math.floor(secondsRemaining / 60);
  const seconds = secondsRemaining % 60;

  async function staySignedIn() {
    setExtending(true);
    try {
      await authApi.heartbeat();
      await poll();
    } finally {
      setExtending(false);
    }
  }

  return (
    <Modal
      open
      // Closing without choosing must not look like "stay signed in".
      onClose={() => void staySignedIn()}
      title={t('auth.idle.title')}
      width="460px"
      footer={
        <>
          <Button variant="secondary" onClick={() => void signOut()}>
            {t('auth.idle.signOutNow')}
          </Button>
          <Button
            variant="primary"
            loading={extending}
            disabled={extending}
            disabledReason={t('auth.idle.extending')}
            onClick={() => void staySignedIn()}
          >
            {t('auth.idle.stay')}
          </Button>
        </>
      }
    >
      <p
        style={{
          margin: 0,
          fontSize: fontSize.md,
          color: color.text.secondary,
          lineHeight: 1.8,
        }}
      >
        {t('auth.idle.body')}
      </p>

      <div
        role="timer"
        aria-live="assertive"
        style={{
          marginTop: space[6],
          textAlign: 'center',
          background: color.status.warning.bg,
          color: color.status.warningStrong,
          borderRadius: '11px',
          padding: '16px',
        }}
      >
        <div style={{ fontSize: fontSize.sm, marginBottom: '4px' }}>{t('auth.idle.remaining')}</div>
        {/* Latin-locked: a clock reorders inside Arabic text. */}
        <Mono size="28px" weight={fontWeight.black}>
          {`${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`}
        </Mono>
      </div>
    </Modal>
  );
}
